// pool/model.js — the Pool page's reading of hass.states. Pure functions,
// tested in dev/pool-model.test.mjs.
//
// What the family asked of it (2026-10-26): "can we swim?", which is mostly
// the water temperature and whether the cleaner is in the way. So: the
// water, the cleaner with Start and Stop, the underwater light and the
// heater. No chemistry.
//
// Readings that lie, and what is read instead:
//   - The eXO's probes are in the pipe, so only a reading taken under flow
//     is the pool. ha-pool's `_settled` sensor holds the last such reading,
//     and the page always says when it was taken.
//   - `binary_sensor.exo_pool_filter_pump` is not a pump-running signal
//     (2026-10-26: off all day while the pump drew 1.3 kW). The page says
//     only what the schedule says, from ha-pool's real-time window (the
//     eXO's own clock runs about 68 minutes slow).
//   - `vacuum.pool_cleaner` reads `docked` through the start of every
//     cycle. Cleaning is the base unit's plug drawing over 15 W; the cloud's
//     calculated status stands in only while the plug is not reporting.
//
// The underwater light and the heater show as "not connected yet" until
// their entities exist and answer; they need no code change to come alive
// beyond naming the heater's entities in HEATER.

export const WATER = {
  temp: 'sensor.pool_water_temperature_settled',
  sch3: 'sensor.pool_exo_sch3_window',
};


export const CLEANER = {
  vacuum: 'vacuum.pool_cleaner',
  status: 'sensor.pool_cleaner_status',
  witness: 'binary_sensor.pool_cleaner_cleaning_local',
  plug: 'switch.pool_cleaner_base_unit',
  lastStart: 'sensor.pool_cleaner_last_cycle_start',
  schedule: 'sensor.pool_cleaner_schedule',
  stop: 'script.pool_cleaner_family_stop',
  start: 'script.pool_cleaner_family_start',
};

export const LIGHT = 'light.pool_pool_lights_293';

// The family's Pause and Run-to-waste (ha-pool pool_family_pump_controls.yaml).
export const PUMP = {
  phase: 'input_select.pool_family_pump_phase',
  offSince: 'input_datetime.pool_family_pump_off_since',
  lastWaste: 'input_text.pool_family_waste_last',
  notified: 'input_boolean.pool_family_pump_unconfirmed_notified',
  latch: 'input_boolean.pool_filtration_stopped',
  timer: 'timer.pool_waste_run',
  stop: 'script.pool_family_pump_stop',
  onWaste: 'script.pool_family_waste_on_waste',
  run: 'script.pool_family_waste_run',
  finish: 'script.pool_family_waste_finish',
  resume: 'script.pool_family_pump_resume',
};
export const WASTE_MINUTES = [5, 10, 15];
// After this long stopped, the card turns rust and the parents are told.
export const UNCONFIRMED_MS = 60 * 60 * 1000;

// The heat pump is not in HA yet: its mains relay (Z-Wave node 294) waits on
// the installer, and whether iAquaLink can set its target is unknown
// (ha-pool HEATER_CONTROL.md). Name the entities here when they exist.
export const HEATER = { power: null, target: null };

// How long after a Stop the card keeps saying "lift it out".
export const STOPPED_FOR_MS = 30 * 60 * 1000;
// How long a Start may take to show as cleaning before the card says so.
export const START_GRACE_MS = 3 * 60 * 1000;

const DEAD = new Set(['unavailable', 'unknown', 'none', '']);
const live = (s) => s != null && !DEAD.has(s.state);
const when = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};
const num = (s) => {
  if (!live(s)) return null;
  const v = parseFloat(s.state);
  return Number.isFinite(v) ? v : null;
};

// ── Formatting ─────────────────────────────────────────────────────

export function fmtClock(d) {
  if (!d) return '';
  const h = d.getHours(), m = d.getMinutes();
  if (m === 0 && h === 12) return 'noon';
  if (m === 0 && h === 0) return 'midnight';
  return `${h % 12 || 12}${m ? `:${String(m).padStart(2, '0')}` : ''} ${h < 12 ? 'am' : 'pm'}`;
}

export function localDate(d) {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
const dayDiff = (a, b) => Math.round((new Date(localDate(b)) - new Date(localDate(a))) / 86400000);
const weekday = (d) => d.toLocaleDateString('en-AU', { weekday: 'long' });

// "at 2:10 pm", "at 7:59 pm yesterday", "on Friday at 6 pm", "on 12 Oct".
export function measuredWhen(at, now) {
  if (!at) return null;
  const n = dayDiff(at, now);
  if (n === 0) return `at ${fmtClock(at)}`;
  if (n === 1) return `at ${fmtClock(at)} yesterday`;
  if (n < 7) return `on ${weekday(at)} at ${fmtClock(at)}`;
  return `on ${at.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}`;
}

// To the nearest five minutes: the robot starts its 5:00 slot at 4:59:28.
export const round5 = (d) => (d ? new Date(Math.round(d.getTime() / 300000) * 300000) : d);

// "This morning, 5 to 7 am", "Yesterday, 5 to 7 am", "Friday, 5 to 7 am".
export function cleanedWhen(start, end, now) {
  if (!start) return null;
  const a = fmtClock(round5(start)), b = fmtClock(round5(end));
  const sa = a.split(' '), sb = b.split(' ');
  const range = sa.length === 2 && sb.length === 2 && sa[1] === sb[1] ? `${sa[0]} to ${b}` : `${a} to ${b}`;
  const n = dayDiff(start, now);
  if (n === 0) return `${start.getHours() < 12 ? 'This morning' : start.getHours() < 17 ? 'This afternoon' : 'This evening'}, ${range}`;
  if (n === 1) return `Yesterday, ${range}`;
  return `${weekday(start)}, ${range}`;
}

// "5 am", "Wednesday, 5 am", "tomorrow, 5 am".
export function nextWhen(at, now) {
  if (!at) return null;
  const n = dayDiff(now, at);
  if (n === 0) return `Today, ${fmtClock(at)}`;
  if (n === 1) return `Tomorrow, ${fmtClock(at)}`;
  return `${weekday(at)}, ${fmtClock(at)}`;
}

// "today at 5 am", "tomorrow at 5 am", "Wednesday at 5 am".
export function nextPhrase(at, now) {
  const n = dayDiff(now, at);
  const day = n === 0 ? 'today' : n === 1 ? 'tomorrow' : weekday(at);
  return `${day} at ${fmtClock(at)}`;
}

// ── Water ──────────────────────────────────────────────────────────

// "HH:MM-HH:MM" in real time, as ha-pool converts it. Null when disabled.
export function pumpWindow(s, now) {
  if (!live(s) || s.attributes?.enabled === false) return null;
  const m = /^(\d\d):(\d\d)-(\d\d):(\d\d)$/.exec(s.state);
  if (!m || s.state === '00:00-00:00') return null;
  const at = (h, mi, days = 0) => {
    const d = new Date(now); d.setHours(+h, +mi, 0, 0); d.setDate(d.getDate() + days); return d;
  };
  let start = at(m[1], m[2]), end = at(m[3], m[4]);
  if (end <= start) end = at(m[3], m[4], 1);               // runs past midnight
  // A window that wrapped and opened yesterday may still be open.
  const yStart = at(m[1], m[2], -1), yEnd = new Date(end.getTime() - 86400000);
  if (now >= yStart && now < yEnd) return { start: yStart, end: yEnd, open: true };
  if (now >= end) { start = at(m[1], m[2], 1); end = new Date(end.getTime() + 86400000); }
  return { start, end, open: now >= start && now < end };
}

export function pumpWords(w, now) {
  if (!w) return 'No run scheduled';
  if (w.open) return `Scheduled until ${fmtClock(w.end)}`;
  const tomorrow = dayDiff(now, w.start) === 1;
  return `Next run ${fmtClock(w.start)}${tomorrow ? ' tomorrow' : ''}`;
}

// The last reading ha-pool's settled sensor took under flow, with when it
// was taken. Not the live probe during the pump's schedule: on 2026-10-26
// the schedule read open 08:00-20:00 while the pump was off from about 08:40
// to 18:00 (a power-cut manual-off after the UPS switchover, inferred), so a
// "live" reading would have been pipe water. Schedule-open is not flow; only
// a pump meter will say the pump is running (ha-pool TODO p1).
export function readWater(states, now) {
  const s = states?.[WATER.temp];
  return {
    temp: num(s),
    at: when(s?.attributes?.sampled_at),
    live: false,
    pump: pumpWindow(states?.[WATER.sch3], now),
  };
}

// A live reading replaces today's point, which otherwise averages whatever
// the settled sensor held.
export function dailyTemps(rows, water = null, now = new Date()) {
  const days = (rows || [])
    .filter((r) => Number.isFinite(r.mean))
    .map((r) => ({ day: new Date(r.start), mean: r.mean }))
    .sort((a, b) => a.day - b.day);
  if (water?.live && water.temp != null) {
    const today = localDate(now);
    const rest = days.filter((d) => localDate(d.day) !== today);
    const day = new Date(now); day.setHours(0, 0, 0, 0);
    return [...rest, { day, mean: water.temp }];
  }
  return days;
}

// A move since yesterday leads; otherwise the week, first day to last.
export function trendWords(days) {
  if (!days || days.length < 2) return null;
  const [p, q] = days.slice(-2);
  const y = Math.round(q.mean - p.mean);
  if (y !== 0 && dayDiff(p.day, q.day) === 1) return `${y > 0 ? 'up' : 'down'} ${Math.abs(y)}° since yesterday`;
  const d = Math.round(days[days.length - 1].mean - days[0].mean);
  if (d === 0) return 'steady this week';
  return `${d > 0 ? 'up' : 'down'} ${Math.abs(d)}° this week`;
}

// ── Cleaner ────────────────────────────────────────────────────────

const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

// The robot's weekly timer, in its own time zone, which is Sydney.
export function nextClean(schedule, now, after = now) {
  const a = schedule?.attributes;
  if (!a) return null;
  for (let i = 0; i < 8; i += 1) {
    const d = new Date(after); d.setDate(d.getDate() + i);
    const slot = a[DAYS[d.getDay()]];
    if (!slot?.isEnabled || slot.time?.hours > 23) continue;
    d.setHours(slot.time.hours, slot.time.minutes || 0, 0, 0);
    if (d > after) return d;
  }
  return null;
}

export function scheduleWords(schedule) {
  const a = schedule?.attributes;
  if (!a) return null;
  const on = DAYS.map((k, i) => ({ k, i, s: a[k] })).filter((x) => x.s?.isEnabled && x.s.time?.hours <= 23);
  if (!on.length) return 'No cleans scheduled.';
  const order = [1, 2, 3, 4, 5, 6, 0];
  on.sort((x, y) => order.indexOf(x.i) - order.indexOf(y.i));
  const names = on.map((x) => x.k.charAt(0).toUpperCase() + x.k.slice(1));
  const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  const times = new Set(on.map((x) => `${x.s.time.hours}:${x.s.time.minutes || 0}`));
  if (times.size === 1) {
    const t = new Date(2000, 0, 1, on[0].s.time.hours, on[0].s.time.minutes || 0);
    return `Cleans ${list} at ${fmtClock(t)}.`;
  }
  return `Cleans ${list}.`;
}

// phase is one of:
//   'asking'    Stop pressed, waiting up to a minute for the plug to drop
//   'cutting'   the cloud stop did not take; mains is off
//   'checking'  mains is back; watching that it stays stopped
//   'starting'  Start pressed and not yet cleaning
//   'cleaning'
//   'powerOff'  mains is off: stopped for certain, no scheduled cleans
//   'stopped'   a Stop finished recently; it may be sitting in the water
//   'unconfirmed' a Stop finished but the plug is not reporting
//   'notStarted' a Start finished a while ago and it never started
//   'idle'
export function readCleaner(states, now) {
  const st = states || {};
  const witness = st[CLEANER.witness]?.state;
  const status = st[CLEANER.status]?.state;
  const plug = st[CLEANER.plug]?.state;
  const stop = st[CLEANER.stop];
  const start = st[CLEANER.start];
  const witnessLive = witness === 'on' || witness === 'off';
  const cleaning = witness === 'on' || (!witnessLive && status === 'cleaning');

  const lastStart = when(st[CLEANER.lastStart]?.state);
  const ct = parseFloat(st[CLEANER.lastStart]?.attributes?.cycleTime);
  const minutes = Number.isFinite(ct) ? ct : 120;
  const lastEnd = lastStart ? new Date(lastStart.getTime() + minutes * 60000) : null;

  const stopAt = when(stop?.attributes?.last_triggered);
  const startAt = when(start?.attributes?.last_triggered);
  const sinceStop = stopAt ? now - stopAt : Infinity;
  const sinceStart = startAt ? now - startAt : Infinity;

  let phase;
  if (stop?.state === 'on') {
    phase = plug === 'off' ? 'cutting' : sinceStop > 65000 ? 'checking' : 'asking';
  } else if (start?.state === 'on') {
    phase = 'starting';
  } else if (plug === 'off') {
    phase = 'powerOff';
  } else if (cleaning) {
    phase = 'cleaning';
  } else if (sinceStart < START_GRACE_MS && sinceStart < sinceStop) {
    phase = 'starting';
  } else if (sinceStop < STOPPED_FOR_MS && sinceStop < sinceStart) {
    phase = witnessLive ? 'stopped' : 'unconfirmed';
  } else if (sinceStart < STOPPED_FOR_MS && sinceStart < sinceStop) {
    phase = 'notStarted';
  } else {
    phase = 'idle';
  }

  const running = phase === 'cleaning' && lastStart && now - lastStart < (minutes + 30) * 60000;
  return {
    phase,
    cleaning,
    witnessLive,
    lastStart,
    lastEnd,
    doneBy: running ? round5(lastEnd) : null,
    next: nextClean(st[CLEANER.schedule], now, cleaning && lastEnd && lastEnd > now ? lastEnd : now),
    schedule: scheduleWords(st[CLEANER.schedule]),
    minutes,
  };
}

// ── Light and heater ───────────────────────────────────────────────

export function readLight(states) {
  const s = states?.[LIGHT];
  return { connected: live(s), on: s?.state === 'on' };
}

export function readHeater(states) {
  const p = HEATER.power ? states?.[HEATER.power] : null;
  const t = HEATER.target ? states?.[HEATER.target] : null;
  return {
    connected: live(p),
    on: p?.state === 'on',
    targetConnected: live(t),
    target: t ? (t.attributes?.temperature ?? num(t)) : null,
  };
}

// ── The page ───────────────────────────────────────────────────────

// ── Pump ───────────────────────────────────────────────────────────

const localTime = (s) => {
  if (!s || DEAD.has(s)) return null;
  const d = new Date(String(s).replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? null : d;
};

// phase: normal | stopping | ready | running | done | paused. A stop made
// elsewhere (the dashboard's STOP sets the same latch) reads as paused, so
// the family can still resume it from here.
export function readPump(states, now) {
  const st = states || {};
  let phase = st[PUMP.phase]?.state;
  if (!['normal', 'stopping', 'ready', 'running', 'done', 'paused'].includes(phase)) phase = 'normal';
  const latched = st[PUMP.latch]?.state === 'on';
  if (phase === 'normal' && latched) phase = 'paused';
  const timer = st[PUMP.timer];
  const finishes = timer?.state === 'active' ? when(timer.attributes?.finishes_at) : null;
  const offSince = localTime(st[PUMP.offSince]?.state);
  const [lastAt, lastMin] = String(st[PUMP.lastWaste]?.state || '').split('|');
  const last = when(lastAt) ? { at: when(lastAt), minutes: parseInt(lastMin, 10) || null } : null;
  const waiting = ['stopping', 'ready', 'done', 'paused'].includes(phase);
  return {
    phase,
    finishes,
    remainingMs: finishes ? Math.max(0, finishes - now) : null,
    offSince,
    unconfirmed: waiting && offSince != null && now - offSince > UNCONFIRMED_MS,
    told: st[PUMP.notified]?.state === 'on' ? when(st[PUMP.notified]?.last_changed) : null,
    last,
    window: pumpWindow(st[WATER.sch3], now),
  };
}

// "7:42" from milliseconds.
export function fmtCountdown(ms) {
  if (ms == null) return '';
  const s = Math.ceil(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

// "Scheduled 7:30 am to 7:30 pm", "Next run 8 am tomorrow", "No run scheduled".
export function scheduleLine(w, now) {
  if (!w) return 'No run scheduled';
  if (w.open || dayDiff(now, w.start) === 0) {
    const a = fmtClock(w.start), b = fmtClock(w.end);
    return `Scheduled ${a} to ${b}`;
  }
  return pumpWords(w, now);
}

export function readPool(states, now) {
  return {
    water: readWater(states, now),
    cleaner: readCleaner(states, now),
    light: readLight(states),
    heater: readHeater(states),
    pump: readPump(states, now),
  };
}

// The line under the title.
export function summary(pool, now) {
  const t = pool.water.temp;
  const water = t != null ? `Water ${Math.round(t)}°` : 'Water temperature unknown';
  const c = pool.cleaner;
  const by = c.doneBy ? ` until about ${fmtClock(c.doneBy)}` : '';
  const head = {
    cleaning: `${water}, the cleaner is running${by}`,
    asking: `${water}, the cleaner is stopping`,
    cutting: `${water}, the cleaner is stopping`,
    checking: `${water}, the cleaner is stopping`,
    starting: `${water}, the cleaner is starting`,
    stopped: `${water}, the cleaner is stopped`,
    powerOff: `${water}, the cleaner is stopped`,
  }[c.phase] || `${water}, the cleaner isn't running`;
  const p = pool.pump?.phase;
  const pumpSub = {
    running: 'The pump is running to waste.',
    stopping: 'The pump is off until the handle is back on Filter.',
    ready: 'The pump is off until the handle is back on Filter.',
    done: 'The pump is off until the handle is back on Filter.',
    paused: 'The pump is paused.',
  }[p];
  const sub = pumpSub || (c.next && c.phase !== 'powerOff' ? `Next clean ${nextPhrase(c.next, now)}.` : '');
  return { head, sub };
}
